import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Eye,
  MapPin,
  Calendar,
  AlertTriangle,
  Copy,
} from "lucide-react";
import { FlightRestrictionZoneResponse } from "@/api/v1/flight-restriction-zone-api";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { apiTypeToLabel } from "@/lib/zone-type-utils";

export const columns: ColumnDef<FlightRestrictionZoneResponse>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => {
      const zone = row.original;
      const name = row.getValue("name") as string;
      return (
        <Link
          to={`/flight-restriction-zones/$zoneSlug`}
          params={{ zoneSlug: zone.designator }}
          className="hover:underline"
        >
          {name}
        </Link>
      );
    },
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => {
      const zone = row.original;
      const typeCode = zone.type; // API code (D, P, R)
      const type = apiTypeToLabel(typeCode); // UI label (Danger, Prohibited, Restricted)

      // Color scheme matching Drone Assist:
      // Prohibited: Red
      // Restricted: Fuchsia/Pink
      // Danger: Orange

      const variant =
        type === "Prohibited"
          ? "destructive" // usually red
          : type === "Restricted"
            ? "default" // We'll style this via class if needed, or use a specific variant if added to badge
            : "secondary"; // usually gray or orange in some themes

      // Custom classes for badges to ensure exact colors
      const badgeClass =
        type === "Restricted"
          ? "bg-red-100 text-red-700 hover:bg-red-100/80 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
          : type === "Danger"
            ? "bg-orange-100 text-orange-700 hover:bg-orange-100/80 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800"
            : type === "Prohibited"
              ? "bg-red-100 text-red-700 hover:bg-red-100/80 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
              : "";

      const icon =
        type === "Prohibited" ? "🚫" : type === "Restricted" ? "⚠️" : "⚠️";

      return (
        <Badge
          variant={variant}
          className={`flex items-center gap-1 ${badgeClass}`}
        >
          <span>{icon}</span>
          {type}
        </Badge>
      );
    },
  },
  {
    accessorKey: "designator",
    header: "Designator",
    cell: ({ row }) => {
      const designator = row.getValue("designator") as string;
      return <div className="text-sm text-muted-foreground">{designator}</div>;
    },
  },
  {
    accessorKey: "effective_begin_time",
    id: "status",
    header: "Status",
    cell: ({ row }) => {
      const zone = row.original;
      const now = new Date();
      const beginTime = new Date(zone.effective_begin_time);
      const endTime = zone.effective_end_time
        ? new Date(zone.effective_end_time)
        : null;
      const isActive = beginTime <= now && (!endTime || endTime >= now);
      return (
        <Badge variant={isActive ? "default" : "secondary"}>
          {isActive ? "Active" : "Inactive"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "effective_begin_time",
    header: "Effective From",
    cell: ({ row }) => {
      const date = row.getValue("effective_begin_time") as string;
      return (
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="h-3 w-3" />
          {new Date(date).toLocaleDateString()}
        </div>
      );
    },
  },
  {
    accessorKey: "effective_end_time",
    header: "Effective To",
    cell: ({ row }) => {
      const date = row.getValue("effective_end_time") as string | null;
      if (!date)
        return <span className="text-muted-foreground">No expiry</span>;

      return (
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="h-3 w-3" />
          {new Date(date).toLocaleDateString()}
        </div>
      );
    },
  },
  {
    accessorKey: "valid_time",
    header: "Valid Time",
    cell: ({ row }) => {
      const date = row.getValue("valid_time") as string;
      return (
        <div className="text-sm text-muted-foreground">
          {new Date(date).toLocaleString()}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const zone = row.original;

      const handleCopyId = async () => {
        try {
          await navigator.clipboard.writeText(zone.id);
          toast.success("Zone ID copied to clipboard");
        } catch {
          toast.error("Failed to copy Zone ID");
        }
      };

      const handleCopyDesignator = async () => {
        try {
          await navigator.clipboard.writeText(zone.designator);
          toast.success("Designator copied to clipboard");
        } catch {
          toast.error("Failed to copy designator");
        }
      };

      const handleCopyCoordinates = async () => {
        try {
          let coordinates: string = "";

          if (zone.polygon && Array.isArray(zone.polygon)) {
            try {
              // Extract first polygon's first ring from coordinates array
              const coords = zone.polygon?.[0]?.[0] as number[][];
              if (coords) {
                coordinates = coords
                  .map((coord: number[]) => `${coord[1]},${coord[0]}`)
                  .join(";");
              }
            } catch {
              toast.error("Invalid geometry data");
              return;
            }
          }

          if (!coordinates) {
            toast.error("No coordinates available");
            return;
          }

          await navigator.clipboard.writeText(coordinates);
          toast.success("Coordinates copied to clipboard");
        } catch {
          toast.error("Failed to copy coordinates");
        }
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={handleCopyId}>
              <Copy className="mr-2 h-4 w-4" />
              Copy ID
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCopyDesignator}>
              <Copy className="mr-2 h-4 w-4" />
              Copy Designator
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCopyCoordinates}>
              <MapPin className="mr-2 h-4 w-4" />
              Copy Coordinates
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={`/flight-restriction-zones/$zoneSlug`} params={{ zoneSlug: zone.designator }}>
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/map" search={{ zone: zone.designator }}>
                <MapPin className="mr-2 h-4 w-4" />
                Show on Map
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                to={`/flight-restriction-zones/$zoneSlug/violations`}
                params={{ zoneSlug: zone.designator }}
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                View Violations
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
